import { ChangeDetectorRef, Component, computed, effect, inject, signal, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { TopicViewStateRecord } from '@common/data-models/records/topic-viewstate-record';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { LayoutState } from '@common/services/layout-state';
import { NavigationService } from '@common/services/navigation-service';
import { Store } from '@ngrx/store';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { ViewStateActions, ViewStateApiActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { initializeRecord } from '@src/suite/common/utils/record-utils';
import { SearchViewStateRecord } from '@suite/common/data-models/records/search-viewstate-record';
import { Util } from '@suite/common/utils/util';

@Component({
    selector: 'st-topic-custom-fields',
    templateUrl: './topic-custom-fields.html',
    styleUrls: ['./topic-custom-fields.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-topic-custom-fields',
    },
})
export class TopicCustomFieldsComponent {
    public data = signal(initializeRecord<StorytellingTopicRecord>(ObjectTypes.TOPIC));
    private store = inject(Store);

    vs = computed(() => this.store.selectSignal(selectCurrentViewState)());

    constructor(
        public globalTablesService: GlobalTablesService,
        public viewStateService: LayoutState,
        public languageSettings: LanguageSettings,
        public dialogRef: MatDialogRef<TopicCustomFieldsComponent>,
        public navigationService: NavigationService,
        public changeDetectorRef: ChangeDetectorRef,
        public nomWebServicesJson: NomWebServicesJson
    ) {
        effect(() => {
            const vs = this.vs();
            if (vs instanceof TopicViewStateRecord || vs instanceof SearchViewStateRecord || vs instanceof PublicationViewStateRecord) {
                this.viewStateCustomCardItems = Util.deepCopy<Array<FieldDef>>(vs.viewStateCustomCardItems);
            }
        });
    }

    public pstFieldNames = PSTFieldNames;
    public viewStateTypes = ViewStateTypes;

    public viewStateCustomCardItems: Array<FieldDef> = [];

    private _topicPropsCache: Array<FieldDef> = [];

    get isSuperVisor(): boolean {
        return this.nomWebServicesJson?.HelloResults?.Supervisor;
    }

    private _topicPropsSimpleCache: Array<{ id: string; title: string }> = [];
    public getList(): Array<FieldDef> {
        if (this._topicPropsCache && this._topicPropsCache.length > 0) return this._topicPropsCache;
        const generatedProps = [];
        const ret = this.globalTablesService.FieldDefs.filter(item => {
            if (item.Validation.toUpperCase() === 'SPECIAL') return false;
            if (item.Validation.toUpperCase() === 'COST') return false;
            if (item.Validation.toUpperCase() === 'ADDRESS') {
                Util.fillAddressFields(item, generatedProps);
            }
            return item.TopicProp === true;
        });
        this._topicPropsCache = [...ret, ...generatedProps];
        return this._topicPropsCache;
    }

    public getCustomFieldsList(): Array<{ id: string; title: string }> {
        if (this._topicPropsSimpleCache && this._topicPropsSimpleCache.length > 0) return this._topicPropsSimpleCache;
        const fields = this.getList();
        this._topicPropsSimpleCache = fields.map(item => ({ id: item.ID, title: item.DefaultCaption }));
        return this._topicPropsSimpleCache;
    }

    public selectionChanged(event: string, idx: number) {
        if (!event) {
            this.viewStateCustomCardItems[idx] = null;
        }
        const field = this.getList().find(item => item.ID === event);
        if (!field) {
            this.viewStateCustomCardItems[idx] = null;
        }
        this.viewStateCustomCardItems[idx] = field;
        this.viewStateCustomCardItems = [...this.viewStateCustomCardItems];
    }

    cancel() {
        this.dialogRef.close();
    }
    apply() {
        this.save();
    }

    save() {
        if (this.vs() instanceof TopicViewStateRecord || this.vs() instanceof SearchViewStateRecord || this.vs() instanceof PublicationViewStateRecord) {
            const fields: Array<FieldDef> = Util.deepCopy<Array<FieldDef>>(this.viewStateCustomCardItems);
            this.store.dispatch(ViewStateActions.updateViewStateCustomCardFields({ fields: fields }));
        }
        this.dialogRef.close(true);
    }

    async saveAsTeamDefault() {
        if (this.vs() instanceof TopicViewStateRecord || this.vs() instanceof SearchViewStateRecord || this.vs() instanceof PublicationViewStateRecord) {
            const fields: Array<FieldDef> = Util.deepCopy<Array<FieldDef>>(this.viewStateCustomCardItems);
            this.store.dispatch(ViewStateApiActions.applyTeamCustomCardFields({ fields: fields, team: this.navigationService.currentPlaybookTeam }));
        }
        this.dialogRef.close(true);
    }

    async saveAsEnterpriseDefault() {
        if (this.vs() instanceof TopicViewStateRecord || this.vs() instanceof SearchViewStateRecord || this.vs() instanceof PublicationViewStateRecord) {
            const fields: Array<FieldDef> = Util.deepCopy<Array<FieldDef>>(this.viewStateCustomCardItems);
            this.store.dispatch(ViewStateApiActions.applyTeamCustomCardFields({ fields: fields, team: this.globalTablesService.EnterprisePlaybookGroup }));
        }
        this.dialogRef.close(true);
    }

    getInnerProp(item: FieldDef) {
        const val = this.data()[item.ID];
        if (val) return val[item.ComplexPropInnerId];
        return '';
    }

    compareFn(c1: FieldDef, c2: FieldDef): boolean {
        return c1 && c2 ? c1.ID === c2.ID : c1 === c2;
    }
}
